import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const BROW_TYPES = {
    en: {
        title: "Which Brow Service is Right for You?",
        subtitle: "Understanding the difference between our brow techniques",
        types: [
            {
                name: "Nano Brows",
                slug: "nano-brows",
                technique: "Machine hair strokes",
                look: "Natural, hair-like strokes that mimic real brow hairs",
                bestFor: "Oily skin, combination skin, mature skin, long-lasting results",
                duration: "2-3 years",
                isPermanent: true,
            },
            {
                name: "Combo Brows",
                slug: "combo-brows",
                technique: "Nano strokes + powder shading",
                look: "Hair strokes in the front, soft shading in the body and tail — best of both worlds",
                bestFor: "Sparse brows wanting natural dimension with fullness, all skin types",
                duration: "2-3 years",
                isPermanent: true,
            },
            {
                name: "Powder Brows",
                slug: "powder-brows",
                technique: "Soft shading technique",
                look: "Soft, filled-in makeup look — like a powder or ombre effect",
                bestFor: "All skin types, especially oily skin, those who wear brow makeup daily",
                duration: "2-3 years",
                isPermanent: true,
            },
            {
                name: "Brow Lamination",
                slug: "brow-lamination",
                technique: "Perm + tint",
                look: "Fluffy, brushed-up brows with natural fullness",
                bestFor: "Those wanting fuller brows without tattoo, existing brow hair required",
                duration: "6-8 weeks",
                isPermanent: false,
            },
        ],
    },
    es: {
        title: "¿Qué Servicio de Cejas es Ideal para Ti?",
        subtitle: "Conoce la diferencia entre nuestras técnicas de cejas",
        types: [
            {
                name: "Nano Brows",
                slug: "nano-brows",
                technique: "Trazos de pelo con máquina",
                look: "Trazos naturales que imitan los pelos reales de las cejas",
                bestFor: "Piel grasa, mixta, madura, resultados duraderos",
                duration: "2-3 años",
                isPermanent: true,
            },
            {
                name: "Combo Brows",
                slug: "combo-brows",
                technique: "Trazos nano + sombreado powder",
                look: "Trazos pelo a pelo al frente, sombreado suave en cuerpo y cola — lo mejor de ambos",
                bestFor: "Cejas escasas buscando dimensión natural con plenitud, todos los tipos de piel",
                duration: "2-3 años",
                isPermanent: true,
            },
            {
                name: "Powder Brows",
                slug: "powder-brows",
                technique: "Técnica de sombreado suave",
                look: "Efecto de maquillaje suave — como polvo u ombré",
                bestFor: "Todo tipo de piel, especialmente grasa, quienes usan maquillaje de cejas a diario",
                duration: "2-3 años",
                isPermanent: true,
            },
            {
                name: "Laminado de Cejas",
                slug: "brow-lamination",
                technique: "Permanente + tinte",
                look: "Cejas esponjosas y peinadas con volumen natural",
                bestFor: "Quienes desean cejas más llenas sin tatuaje, se requiere vello existente",
                duration: "6-8 semanas",
                isPermanent: false,
            },
        ],
    },
};

const BrowComparison = ({ currentService }) => {
    const { lang } = useLanguage();
    const content = BROW_TYPES[lang] || BROW_TYPES.en;

    return (
        <section data-testid="brow-comparison" className="bg-[#F5F2E4] py-24 md:py-32">
            <div className="max-w-[1400px] mx-auto px-6">
                <div className="text-center mb-12">
                    <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                        {lang === "es" ? "Comparación" : "Compare"}
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#3D3D24] leading-[1.05] mb-4">
                        {content.title}
                    </h2>
                    <p className="text-[#3D3D24]/70 text-base max-w-2xl mx-auto">
                        {content.subtitle}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {content.types.map((type, i) => {
                        const isCurrentService = type.slug === currentService;
                        return (
                            <div
                                key={i}
                                data-testid={`brow-compare-${type.slug}`}
                                className={`p-8 transition-all ${
                                    isCurrentService
                                        ? "bg-[#3D3D24] text-[#FFFFF3]"
                                        : "bg-white border border-[#3D3D24]/10 hover:border-[#3D3D24]/30"
                                }`}
                            >
                                <div className={`font-label text-[10px] mb-3 ${isCurrentService ? "text-[#D4A95A]" : "text-[#D4A95A]"}`}>
                                    {type.isPermanent ? (lang === "es" ? "Permanente" : "Permanent") : (lang === "es" ? "Temporal" : "Temporary")}
                                </div>
                                <h3 className={`font-serif text-2xl md:text-3xl mb-4 ${isCurrentService ? "" : "text-[#3D3D24]"}`}>
                                    {type.name}
                                </h3>

                                <div className="space-y-4 mb-6">
                                    <div>
                                        <div className={`font-label text-[10px] mb-1 ${isCurrentService ? "opacity-70" : "text-[#3D3D24]/60"}`}>
                                            {lang === "es" ? "Técnica" : "Technique"}
                                        </div>
                                        <p className={`text-sm ${isCurrentService ? "opacity-90" : "text-[#3D3D24]/85"}`}>
                                            {type.technique}
                                        </p>
                                    </div>
                                    <div>
                                        <div className={`font-label text-[10px] mb-1 ${isCurrentService ? "opacity-70" : "text-[#3D3D24]/60"}`}>
                                            {lang === "es" ? "Resultado" : "Look"}
                                        </div>
                                        <p className={`text-sm ${isCurrentService ? "opacity-90" : "text-[#3D3D24]/85"}`}>
                                            {type.look}
                                        </p>
                                    </div>
                                    <div>
                                        <div className={`font-label text-[10px] mb-1 ${isCurrentService ? "opacity-70" : "text-[#3D3D24]/60"}`}>
                                            {lang === "es" ? "Ideal Para" : "Best For"}
                                        </div>
                                        <p className={`text-sm ${isCurrentService ? "opacity-90" : "text-[#3D3D24]/85"}`}>
                                            {type.bestFor}
                                        </p>
                                    </div>
                                    <div>
                                        <div className={`font-label text-[10px] mb-1 ${isCurrentService ? "opacity-70" : "text-[#3D3D24]/60"}`}>
                                            {lang === "es" ? "Duración" : "Lasts"}
                                        </div>
                                        <p className={`text-sm font-medium ${isCurrentService ? "text-[#D4A95A]" : "text-[#D4A95A]"}`}>
                                            {type.duration}
                                        </p>
                                    </div>
                                </div>

                                {!isCurrentService && (
                                    <Link
                                        to={`/${type.slug}`}
                                        className="inline-flex items-center gap-2 text-sm font-label text-[#3D3D24] hover:text-[#D4A95A] transition-colors"
                                    >
                                        {lang === "es" ? "Ver Servicio" : "View Service"}
                                        <ArrowRight size={14} />
                                    </Link>
                                )}
                                {isCurrentService && (
                                    <div className="font-label text-[10px] text-[#D4A95A] tracking-[0.2em]">
                                        {lang === "es" ? "ESTÁS AQUÍ" : "YOU ARE HERE"}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default BrowComparison;
